import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, TrendingUpIcon, ArrowDownIcon, ClockIcon, StarIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent } from '../../utils/analytics';
import { atnaHeroCopy as c, atnaBenefits, type Bi, type BenefitKey } from '../../data/atnaHero';
import { AtnaFlow } from './AtnaFlow';
const EASE = [0.23, 1, 0.32, 1] as const;
const BENEFIT_ICONS: Record<BenefitKey, typeof StarIcon> = {
  capacity: TrendingUpIcon,
  manual: ArrowDownIcon,
  speed: ClockIcon,
  quality: StarIcon
};
export function AtnaHero() {
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  return <section className="relative overflow-hidden bg-[#FDFDFF] pt-16 lg:pt-20 pb-20 lg:pb-24 px-6" data-id="element-917" data-mp="1kwickqm4534">
      <div className="pointer-events-none absolute -top-40 right-[-10%] w-[900px] h-[700px] rounded-full bg-[#EEEDFD] opacity-70 blur-[160px]" aria-hidden="true" data-id="element-918" data-mp="z1v87wh1r4fk" />
      <div className="pointer-events-none absolute top-1/3 left-[40%] w-[520px] h-[420px] rounded-full bg-[#EEF3FD] opacity-60 blur-[140px]" aria-hidden="true" data-id="element-919" data-mp="2p7ed01lj8dkc" />

      <div className="relative max-w-[1520px] mx-auto" data-id="element-920" data-mp="2fhutctpzwfof">
        <div className="grid grid-cols-1 2xl:grid-cols-[40fr_60fr] gap-14 2xl:gap-10 items-center" data-id="element-921" data-mp="2fh1ra61zeecm">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: EASE
        }} className="min-w-0" data-id="element-922" data-mp="2kfd48pnkp4z2">
            <p className="font-sans text-[13px] md:text-[14px] font-medium tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-923" data-mp="2ak32m3136zlt">
              {pick(c.eyebrow)}
            </p>
            <h1 className="font-serif text-[2.5rem] sm:text-[3.3rem] lg:text-[3.7rem] 2xl:text-[3.9rem] leading-[1.0] tracking-tight mt-6" data-id="element-924" data-mp="21hacw47jyvy3">
              {c.headline.map(l => <span key={l.text.fr} className={`block ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-925" data-mp="3vwmnpc6pbx0d">
                  {pick(l.text)}
                </span>)}
            </h1>
            <p className="font-sans text-[16px] md:text-[17.5px] text-[#4A5268] leading-relaxed mt-6 max-w-[600px]" data-id="element-926" data-mp="3pfbd45xk3tuc">
              {pick(c.body)}
            </p>
            <div className="mt-10" data-id="element-927" data-mp="3kiudofkwpt1y">
              <Link to="/request-demo" onClick={() => trackEvent('Demo CTA Clicked', {
              location: 'ATNA Hero'
            })} className="inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-xl bg-[#4F46E5] px-10 py-3.5 font-sans text-[16px] font-medium text-white shadow-[0_14px_30px_-14px_rgba(79,70,229,0.8)] transition-colors duration-150 hover:bg-[#4338CA] whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5] focus-visible:ring-offset-2" data-id="element-928" data-mp="3fi6olzg8nmad">
                {pick(c.secondary)}
                <ArrowRightIcon className="w-5 h-5" strokeWidth={2} aria-hidden="true" data-id="element-929" data-mp="uzsq71vx6b25" />
              </Link>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: 0.08,
          ease: EASE
        }} className="min-w-0" data-id="element-930" data-mp="139rneeughlam">
            <AtnaFlow pick={pick} data-id="element-931" data-mp="1c7to0taa2bxd" />
          </motion.div>
        </div>

        <ul className="mt-16 pt-7 border-t border-[#ECEBF5] grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-y-6" data-id="element-932" data-mp="1h90tgloy6z0v">
          {atnaBenefits.map((b, i) => {
          const Icon = BENEFIT_ICONS[b.key];
          return <li key={b.key} className={`flex items-center gap-5 xl:px-8 ${i > 0 ? 'xl:border-l xl:border-[#ECEBF5]' : 'xl:pl-2'}`} data-id="element-933" data-mp="1q6t95qyl08os" data-mpi={i}>
                <span className="w-[54px] h-[54px] rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" data-id="element-934" data-mp="1zy7h8n4lmgtj">
                  <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-935" data-mp="24vphlp4jvnpx" />
                </span>
                <span className="font-sans text-[15px] md:text-[15.5px] leading-snug text-[#2A3150]" data-id="element-936" data-mp="36oa3pg23izq7">
                  {b.label.map(l => <span key={l.fr} className="block" data-id="element-937" data-mp="31oe3lrwirsfx">{pick(l)}</span>)}
                </span>
              </li>;
        })}
        </ul>
      </div>
    </section>;
}